import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { KanbanSquare, List, Plus, Search } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Pipeline } from "./Pipeline";
import { ClientsTable } from "./ClientsTable";
import { ClientDialog } from "./ClientDialog";
import { ClientSheet } from "./ClientSheet";
import { DueTasksCard } from "./DueTasksCard";

export const AdminCrm = ({ partners }) => {
  const [params, setParams] = useSearchParams();
  const [clients, setClients] = useState([]);
  const [view, setView] = useState("pipeline");
  const [q, setQ] = useState("");
  const [partner, setPartner] = useState("all");
  const [openId, setOpenId] = useState(params.get("client"));
  const [dialog, setDialog] = useState({ open: false, client: null });
  const [tick, setTick] = useState(0);
  const load = useCallback(() => { api.get("/crm/clients").then((r) => setClients(r.data)); setTick((t) => t + 1); }, []);
  useEffect(() => { load(); }, [load]);

  const shown = useMemo(() => {
    const s = q.trim().toLowerCase();
    return clients.filter((c) => (partner === "all" || (c.partner_id || "genis") === partner)
      && (!s || [c.company, c.contact_name, c.email, c.phone, c.city].some((v) => (v || "").toLowerCase().includes(s))));
  }, [clients, q, partner]);

  const move = async (id, stage) => {
    setClients((cs) => cs.map((c) => (c.id === id ? { ...c, stage } : c)));
    try { await api.patch(`/crm/clients/${id}/stage`, { stage }); } catch { toast.error("Impossible de changer l'étape"); load(); }
  };

  return (
    <div className="space-y-4" data-testid="admin-crm">
      <DueTasksCard onOpenClient={setOpenId} refreshKey={tick} />
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un client, contact, email, ville…" className="pl-9" data-testid="crm-search" />
        </div>
        <Select value={partner} onValueChange={setPartner}>
          <SelectTrigger className="w-52" data-testid="crm-partner-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les partenaires</SelectItem>
            <SelectItem value="genis">Genis Energie (direct)</SelectItem>
            {partners.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="flex rounded-lg border border-border p-0.5">
          <Button size="sm" variant={view === "pipeline" ? "secondary" : "ghost"} onClick={() => setView("pipeline")} data-testid="crm-view-pipeline"><KanbanSquare className="h-4 w-4 mr-1" /> Pipeline</Button>
          <Button size="sm" variant={view === "list" ? "secondary" : "ghost"} onClick={() => setView("list")} data-testid="crm-view-list"><List className="h-4 w-4 mr-1" /> Liste</Button>
        </div>
        <Button className="bg-genis hover:bg-genis-dark text-white" onClick={() => setDialog({ open: true, client: null })} data-testid="crm-new-client"><Plus className="h-4 w-4 mr-1" /> Nouveau client</Button>
      </div>
      {view === "pipeline"
        ? <Pipeline clients={shown} partners={partners} onMove={move} onOpen={setOpenId} />
        : <ClientsTable clients={shown} partners={partners} onOpen={setOpenId} />}
      <ClientSheet clientId={openId} refreshKey={tick} partners={partners} onClose={() => { setOpenId(null); if (params.get("client")) { params.delete("client"); setParams(params, { replace: true }); } }} onChanged={load} onEdit={(c) => setDialog({ open: true, client: c })} />
      <ClientDialog open={dialog.open} client={dialog.client} partners={partners} onOpenChange={(o) => setDialog((d) => ({ ...d, open: o }))}
        onSaved={(c) => { setDialog({ open: false, client: null }); load(); setOpenId(c.id); }} />
    </div>
  );
};
